import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Bell, Check, Trash2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

function Notificaciones() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const isAdmin = user?.role === 'entrenador';

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error('Error al marcar todas:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await axios.delete(`${API}/notifications`);
      setNotifications([]);
      setShowDeleteAllDialog(false);
    } catch (error) {
      console.error('Error al eliminar todas:', error);
    }
  };

  const cleanupOldNotifications = async () => {
    try {
      const response = await axios.delete(`${API}/notifications/old/7`);
      alert(response.data.message);
      fetchNotifications();
      setShowCleanupDialog(false);
    } catch (error) {
      console.error('Error al limpiar:', error);
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'plaza_abierta':
        return '🎫';
      case 'recordatorio':
        return '⏰';
      case 'nuevo_registro':
        return '🆕';
      case 'cumpleanos':
        return '🎂';
      case 'entreno_cancelado':
        return '❌';
      case 'nueva_noticia':
        return '📰';
      case 'galon':
        return '🏅';
      case 'hall_of_fame':
        return '🏆';
      case 'mkdietal':
        return '🥗';
      default:
        return '📢';
    }
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="notificaciones-title">
              Notificaciones
            </h1>
            <p className="text-gray-400">
              {notifications.filter(n => !n.leida).length} sin leer · {notifications.length} total
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {notifications.some(n => !n.leida) && (
              <Button 
                onClick={markAllAsRead}
                className="btn-primary-mk"
                data-testid="btn-mark-all-read"
              >
                <Check className="mr-2 h-4 w-4" />
                Marcar leídas
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                onClick={() => setShowDeleteAllDialog(true)}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500/10"
                data-testid="btn-delete-all"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar todas
              </Button>
            )}
            {isAdmin && (
              <Button 
                onClick={() => setShowCleanupDialog(true)}
                variant="outline"
                className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                data-testid="btn-cleanup"
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Limpiar antiguas
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : notifications.length === 0 ? (
          <Card className="card-mk border-gray-800">
            <CardContent className="p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">No tienes notificaciones</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card 
                key={notification.id} 
                className={`card-mk ${notification.leida ? 'bg-gray-900' : 'bg-gray-800 border-gold'}`}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">
                      {getTipoIcon(notification.tipo)}
                    </div>
                    <div className="flex-1">
                      <p className={`${notification.leida ? 'text-gray-400' : 'text-white font-semibold'}`}>
                        {notification.mensaje}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        {format(new Date(notification.fecha), "d 'de' MMMM, HH:mm", { locale: es })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!notification.leida && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markAsRead(notification.id)}
                          className="bg-gold text-gray-900 border-gold hover:bg-yellow-600"
                          data-testid="btn-mark-read"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteNotification(notification.id)}
                        className="border-red-500 text-red-500 hover:bg-red-500/20"
                        data-testid="btn-delete-notification"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog: Eliminar todas las notificaciones */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gold">¿Eliminar todas las notificaciones?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300">
            Se eliminarán permanentemente todas tus {notifications.length} notificaciones. Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteAllDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={deleteAllNotifications}
            >
              Eliminar todas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Limpiar notificaciones antiguas (solo admin) */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gold">Limpiar notificaciones antiguas</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300">
            Se eliminarán las notificaciones de <strong>todos los usuarios</strong> que tengan más de 7 días de antigüedad. Esto ayuda a mantener la base de datos limpia.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCleanupDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={cleanupOldNotifications}
            >
              Limpiar antiguas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Notificaciones;
